/** @file ChoiceRow: a labelled row of toggle buttons picking one value (none picked until the user clicks). */
import type { ReactElement, ReactNode } from 'react';

/** One choice: its value, its accessible name, an optional picture and short caption, and whether it may be picked. */
export type Choice<T extends string | number> = { value: T; label: string; icon?: ReactNode; caption?: string; disabled?: boolean };

/** The group label, the choices, the picked value (null = none yet) and the pick callback. */
export type ChoiceRowProps<T extends string | number> = { label: string; choices: Choice<T>[]; value: T | null; onChange: (value: T) => void };

/** A `role="group"` of `aria-pressed` buttons; the picked one is highlighted like a pressed tool button. */
export function ChoiceRow<T extends string | number>({ label, choices, value, onChange }: ChoiceRowProps<T>): ReactElement {
  return (
    <div className="p3-field">
      <span>{label}</span>
      <div className="p3-row" role="group" aria-label={label}>
        {choices.map((c) => (
          <button key={String(c.value)} type="button" className="p3-btn p3-icon-btn p3-choice" aria-label={c.label} title={c.label}
            aria-pressed={c.value === value} disabled={c.disabled} onClick={() => { onChange(c.value); }}>
            {c.icon}
            {c.caption === undefined ? null : <span>{c.caption}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}
